import React, { useState, useEffect, useMemo } from 'react';
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { useNavigate } from 'react-router-dom';
import { EIDIKI_TABLE_OPTIONS_D } from './constants';
import { getHost } from './config';
import FilterSidebar from './FilterSidebar';
import {
    initialSidebarOpen,
    collapseFilterSidebarOnMobile,
    formatYearRangeLabel,
    joinFilterSummaryParts,
} from './filterSidebarUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./custom.scss";

function RankingPerState() {
    const [data, setData] = useState(null);
    const [uniqueDates, setUniqueDates] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [uniqueLocations, setUniqueLocations] = useState(null);
    const [yearRange, setYearRange] = useState(''); // New state for year range
    const [specialty, setSpecialty] = useState(''); // New state for specialty
    const [eidikiAgogi, setEidikiAgogi] = useState('false'); // New state for eidiki_agogi
    const [specialtyOptions, setSpecialtyOptions] = useState([]); // Options for specialty dropdown
    const [specialtyLoading, setSpecialtyLoading] = useState(false); // Loading state for specialty fetch
    const [loading, setLoading] = useState(false); // New loading state
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    // Function to handle logout
    const handleLogout = () => {
        // Clear the authToken from localStorage
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());

        // Redirect to the login page
        navigate('/', { replace: true });
    }
    
    // Fetch specialties based on the selected year_range
    useEffect(() => {
        let ignore = false;
        if (yearRange) { // Fetch only if yearRange is selected
            setSpecialtyLoading(true);
            const url = `${getHost()}/api/specialty-types?year_range=${yearRange}`;
            fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => response.json())
                .then(data => { if (!ignore) setSpecialtyOptions(data.results); })
                .catch(error => console.error('Error fetching specialties:', error))
                .finally(() => { if (!ignore) setSpecialtyLoading(false); });
        } else {
            setSpecialtyOptions([]); // Clear specialties when yearRange is not selected
        }
        return () => { ignore = true; };
    }, [yearRange]); // Trigger fetch when yearRange changes

    // Handle form submission to fetch data based on the selected filters
    const handleSubmit = () => {
        setLoading(true);
        const url = `${getHost()}/api/states-min-max`;
        const requestBody = {
            year_range: yearRange,
            specialty: specialty,
            eidiki_agogi: eidikiAgogi === 'true', // Send boolean value

        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        })
            .then(response => response.json())
            .then(data => {
                setData(data.results);
                const dates = new Set();
                const locations = new Set();
                data.results.forEach(locationData => {
                    Object.keys(locationData).forEach(location => {
                        locations.add(location);
                        Object.keys(locationData[location]).forEach(date => {
                            dates.add(date);
                        });
                    });
                });
                setUniqueDates(Array.from(dates).sort());
                setUniqueLocations(Array.from(locations).sort());
                collapseFilterSidebarOnMobile(setSidebarOpen);
            })
            .catch(error => console.error('Error fetching data:', error))
            .finally(() => {
                setLoading(false); // Hide loading indicator regardless of success/failure
            });
    };

    const handleDateChange = (event) => {
        const value = event.target.value;
        setSelectedDate(value);
        const selectedDate = event.target.value;
        if (selectedDate === "all") {
            setSelectedDates(uniqueDates);
        } else {
            setSelectedDates([selectedDate]);
        }
    };

    const handleLocationChange = (event) => {
        setSelectedLocation(event.target.value);
    };

    const filterDataByDates = (data, dates) => {
        return data.filter(locationData => {
            if (!selectedLocation) return true;
            return Object.keys(locationData)[0] === selectedLocation;
        }).map(locationData => {
            const filteredInfo = {};
            Object.keys(locationData[Object.keys(locationData)[0]]).forEach(date => {
                if (dates.includes(date)) {
                    filteredInfo[date] = locationData[Object.keys(locationData)[0]][date];
                }
            });
            return { [Object.keys(locationData)[0]]: filteredInfo };
        });
    };

    const isFormValid = yearRange !== '' && specialty !== '';

    const filterSummary = useMemo(
        () => joinFilterSummaryParts([formatYearRangeLabel(yearRange), specialty]),
        [yearRange, specialty]
    );

    return (
        <div id="root">
            <Header handleLogout={handleLogout}/>
            <main className='main-container ranking-per-state-page'>
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="ranking-per-state-sidebar-body"
                >
                        <Form>
                                    <Form.Group className="mb-3" controlId="yearSelect.range">
                                        <Form.Label className='custom-label'>Έτος:</Form.Label>
                                        <Form.Select
                                            className="custom-form"
                                            value={yearRange}
                                            onChange={(e) => {
                                                setYearRange(e.target.value);
                                                setSpecialty("");
                                                setSelectedLocation("");
                                                setSelectedDates([]);
                                                setSelectedDate("");
                                                setData(null);
                                            }}>
                                            <option>Επιλέξτε...</option>
                                            <option value="2022_2023">2022-2023</option>
                                            <option value="2023_2024">2023-2024</option>
                                            <option value="2024_2025">2024-2025</option>
                                            <option value="2025_2026">2025-2026</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="yearSelect.specialty">
                                        <Form.Label className='custom-label'>Ειδικότητα:</Form.Label>
                                        <Form.Select
                                            className="custom-form"
                                            value={specialty}
                                            onChange={(e) => {
                                                setSpecialty(e.target.value);
                                                setSelectedLocation("");
                                                setSelectedDates([]);
                                                setSelectedDate("");
                                                setData(null);
                                            }}
                                            disabled={!yearRange || specialtyLoading}>
                                            <option>{specialtyLoading ? 'Φόρτωση...' : 'Επιλέξτε...'}</option>
                                            {!specialtyLoading && (specialtyOptions.length > 0 ? (
                                                specialtyOptions
                                                    .slice()
                                                    .sort((a, b) => a.localeCompare(b))
                                                    .map((spec) => (
                                                        <option key={spec} value={spec}>
                                                            {spec}
                                                        </option>
                                                    ))
                                            ) : (
                                                <option>No specialties available</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="yearSelect.eidiki">
                                        <Form.Label className='custom-label'>Ειδική Αγωγή:</Form.Label>
                                        <Form.Select
                                            className="custom-form"
                                            value={eidikiAgogi}
                                            onChange={(e) => {
                                                setEidikiAgogi(e.target.value);
                                                setSelectedLocation("");
                                                setSelectedDates([]);
                                                setSelectedDate("");
                                                setData(null);
                                            }}
                                        >
                                            <option value="false">Όχι</option>
                                            <option value="true">Ναι</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Form>
                                <p className="ranking-per-state-sidebar-note text-muted fst-italic small">
                                    Αν επιλέξετε <b>Ειδική αγωγή: Ναι</b>, οι θέσεις μπορεί να προέρχονται από: <b>Κύριο</b> ή <b>Επικουρικό</b> πίνακα ΕΑΕ ή πίνακα <b>Γενικής</b> αγωγής.
                                </p>
                                <div className="d-flex justify-content-center mt-2">
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        className="predict-submit-button"
                                        onClick={handleSubmit}
                                        disabled={!isFormValid}
                                    >
                                        Υποβολή
                                    </Button>
                                </div>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                {!data && !loading && (
                                    <p className="text-muted small mb-2">
                                        Επιλέγοντας το <b>Έτος</b>, την <b>Ειδικότητα</b> και την <b>Περιοχή Προτίμησης</b>, ο εκπαιδευτικός έχει τη δυνατότητα να δει άμεσα τον <b>Συνολικό αριθμό προσλήψεων</b> ανά ημερομηνία πρόσληψης για την επιλεγμένη σχολική χρονιά. Επίσης, μπορεί εύκολα να δει τη θέση του πρώτου και τελευταίου προσληφθέντα ανά ημερομηνία πρόσληψης.<br/>
                                        <span style={{ paddingTop: '2px', display: 'inline-block' }}>
                                            <b>Διευκρίνιση:</b> Οι θέσεις του πρώτου και τελευταίου αναφέρονται στους αρχικούς πίνακες και όχι τους εκκαθαρισμένους.
                                        </span>
                                    </p>
                                )}
                                {loading && (
                                    <div className="text-center mt-3 mb-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div>Φόρτωση αποτελεσμάτων...</div>
                                    </div>
                                )}

                                {data ? (
                                    <div className={!sidebarOpen ? 'results-snug-mobile' : ''}>
                                        <Form className="ranking-per-state-result-filters">
                                            <Row className="g-2 g-sm-3">
                                                <Col xs={12} sm={6} md={5} lg={4}>
                                                    <Form.Group className="mb-2 mb-sm-0" controlId="yearSelect.location">
                                                        <Form.Label className="custom-label ranking-per-state-result-label">Διεύθυνση Εκπαίδευσης:</Form.Label>
                                                        <Form.Select
                                                            className="custom-form form-select form-select-sm ranking-per-state-result-select"
                                                            value={selectedLocation}
                                                            onChange={(e) => {
                                                                setSelectedLocation(e.target.value);
                                                                setSelectedDates([]);
                                                                setSelectedDate("");
                                                            }}
                                                        >
                                                            <option value="">Επιλέξτε...</option>
                                                            {uniqueLocations.map(location => (
                                                                <option key={location} value={location}>{location}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={12} sm={6} md={5} lg={4}>
                                                    <Form.Group className="mb-2 mb-sm-0" controlId="yearSelect.date">
                                                        <Form.Label className="custom-label ranking-per-state-result-label">Ημερομηνία φάσης προσλήψεων:</Form.Label>
                                                        <Form.Select className="custom-form form-select form-select-sm ranking-per-state-result-select" value={selectedDate} onChange={handleDateChange}>
                                                            <option value="">Επιλέξτε...</option>
                                                            <option value="all">Όλες οι ημερομηνίες</option>
                                                            {[...uniqueDates]
                                                                .sort((a, b) => {
                                                                    const parseDate = str => {
                                                                        const [day, month, year] = str.split('-');
                                                                        return new Date(`${year}-${month}-${day}`);
                                                                    };
                                                                    return parseDate(a) - parseDate(b);
                                                                })
                                                                .map(date => (
                                                                    <option key={date} value={date}>{date}</option>
                                                                ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>

                                        {selectedDates.length > 0 && selectedLocation && (
                                            <div className="data-container">
                                                {filterDataByDates(data, selectedDates).map(locationData => (
                                                    <React.Fragment key={Object.keys(locationData)[0]}>
                                                        {Object.entries(locationData[Object.keys(locationData)[0]]).map(([date, info]) => (
                                                            <Card className='custom-card' key={date}>
                                                                <Card.Body>
                                                                    <Card.Title>Ημερομηνία: {date}</Card.Title>
                                                                    <ListGroup className="list-group-flush">
                                                                        <ListGroup.Item>
                                                                            Σειρά Πρώτου: {info.table_min}
                                                                            {eidikiAgogi === "true" && info.table_min_type !== null && ` (${EIDIKI_TABLE_OPTIONS_D[info.table_min_type]})`}
                                                                        </ListGroup.Item>
                                                                        {info.table_min_score !== null && <ListGroup.Item>
                                                                            Μόρια Πρώτου: {info.table_min_score}
                                                                        </ListGroup.Item>}
                                                                        <ListGroup.Item>
                                                                            Σειρά Τελευταίου: {info.table_max}
                                                                            {eidikiAgogi === "true" && info.table_max_type !== null && ` (${EIDIKI_TABLE_OPTIONS_D[info.table_max_type]})`}
                                                                        </ListGroup.Item>
                                                                        {info.table_max_score !== null && <ListGroup.Item>
                                                                            Μόρια Τελευταίου: {info.table_max_score}
                                                                        </ListGroup.Item>}
                                                                        <ListGroup.Item>Συνολικές προσλήψεις: {info.total}</ListGroup.Item>
                                                                    </ListGroup>
                                                                </Card.Body>
                                                            </Card>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        )}
                                        </div>
                                    ) : !loading && null}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </main>
            
            <Footer />
        </div>
    );
}

export default RankingPerState;
